import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Alert,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as ActivateIcon,
  Pause as PauseIcon,
  TrendingUp as TrendingUpIcon,
  RemoveRedEye as ViewsIcon,
  TouchApp as ClicksIcon,
  Assessment as AssessmentIcon,
} from "@mui/icons-material";
import useAdminContext from "../../store/contexts/AdminContext";
import CreateAdModal from "./CreateAdModal";
import EditAdModal from "./EditAdModal";
import AdDetailsModal from "./AdDetailsModal";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import { ADMIN_ROLES } from "../../constants/types";

const AdManagement = () => {
  // ============= CONTEXT =============
  const {
    admin,
    getAdminAdsApiCall,
    getAdAnalyticsApiCall,
    deleteAdApiCall,
    activateAdApiCall,
    pauseAdApiCall,
    authState,
    showSnackbar,
  } = useAdminContext();

  const { loading } = authState;

  // ============= RBAC HELPER =============
  const isSuperAdmin = () => admin?.role === ADMIN_ROLES.SUPER_ADMIN;

  // ============= STATE MANAGEMENT =============
  const [ads, setAds] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedAd, setSelectedAd] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // ============= DATA FETCHING =============
  const fetchAds = useCallback(async () => {
    try {
      setErrorMessage("");
      const response = await getAdminAdsApiCall();
      if (response) {
        setAds(response.ads || response || []);
      }
    } catch (err) {
      setErrorMessage("Failed to fetch ads");
      console.error("Error fetching ads:", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await getAdAnalyticsApiCall();
      if (response) {
        setAnalytics(response.analytics || response);
      }
    } catch (err) {
      console.error("Error fetching analytics:", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAds();
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============= COMPUTED VALUES =============
  const filteredAds = ads.filter((ad) => {
    const matchesSearch =
      ad.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ad.description?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || ad.status?.toLowerCase() === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // ============= RBAC PROTECTED EVENT HANDLERS =============
  const handleCreateSuccess = () => {
    if (!isSuperAdmin()) {
      showSnackbar("Only super admins can create ads", "error");
      return;
    }
    setSuccessMessage("Ad created successfully!");
    fetchAds();
    fetchAnalytics();
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleEditSuccess = () => {
    if (!isSuperAdmin()) {
      showSnackbar("Only super admins can edit ads", "error");
      return;
    }
    setSuccessMessage("Ad updated successfully!");
    fetchAds();
    fetchAnalytics();
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleActivateAd = async (adId) => {
    if (!isSuperAdmin()) {
      showSnackbar("Only super admins can activate ads", "error");
      return;
    }
    const result = await activateAdApiCall(adId);
    if (result) {
      fetchAds();
      fetchAnalytics();
    }
  };

  const handlePauseAd = async (adId) => {
    if (!isSuperAdmin()) {
      showSnackbar("Only super admins can pause ads", "error");
      return;
    }
    const result = await pauseAdApiCall(adId);
    if (result) {
      fetchAds();
      fetchAnalytics();
    }
  };

  const handleDeleteAd = async () => {
    if (!selectedAd) return;

    if (!isSuperAdmin()) {
      showSnackbar("Only super admins can delete ads", "error");
      return;
    }

    const result = await deleteAdApiCall(selectedAd.id);
    if (result) {
      setOpenDeleteDialog(false);
      setSelectedAd(null);
      fetchAds();
      fetchAnalytics();
    }
  };

  const handleViewDetails = (ad) => {
    setSelectedAd(ad);
    setOpenDetailsModal(true);
  };

  const handleEditAd = (ad) => {
    if (!isSuperAdmin()) {
      showSnackbar("Only super admins can edit ads", "error");
      return;
    }
    setSelectedAd(ad);
    setOpenEditModal(true);
  };

  const handleDeleteClick = (ad) => {
    if (!isSuperAdmin()) {
      showSnackbar("Only super admins can delete ads", "error");
      return;
    }
    setSelectedAd(ad);
    setOpenDeleteDialog(true);
  };

  const handleCreateAdClick = () => {
    if (!isSuperAdmin()) {
      showSnackbar("Only super admins can create ads", "error");
      return;
    }
    setOpenCreateModal(true);
  };

  // ============= UTILITY FUNCTIONS =============
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "success";
      case "paused":
        return "warning";
      case "completed":
        return "info";
      case "draft":
        return "default";
      default:
        return "default";
    }
  };

  const calculateProgress = (impressions, goal) => {
    if (!goal) return 0;
    return Math.min((impressions / goal) * 100, 100);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || "0";
  };

  // ============= RENDER =============
  return (
    <Box>
      {/* Header Section */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: 600, color: "primary.main", mb: 1 }}
          >
            Ad Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isSuperAdmin()
              ? "Create, manage, and track your advertising campaigns"
              : "View advertising campaigns (Read-only access)"}
          </Typography>
        </Box>
        {isSuperAdmin() && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateAdClick}
            sx={{
              px: 3,
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              boxShadow: 2,
            }}
          >
            Create New Ad
          </Button>
        )}
      </Box>

      {/* Messages */}
      {errorMessage && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          onClose={() => setErrorMessage("")}
        >
          {errorMessage}
        </Alert>
      )}
      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setSuccessMessage("")}
        >
          {successMessage}
        </Alert>
      )}

      {/* RBAC Warning for Regular Admins */}
      {!isSuperAdmin() && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You have read-only access to advertisements. Only super admins can
          create, edit, or delete ads.
        </Alert>
      )}

      {/* Analytics Cards */}
      {analytics && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: "100%", borderRadius: 2, boxShadow: 2 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <AssessmentIcon
                    sx={{ color: "primary.main", fontSize: 40, mr: 2 }}
                  />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {analytics.totalAds || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Ads
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: "100%", borderRadius: 2, boxShadow: 2 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <ViewsIcon sx={{ color: "info.main", fontSize: 40, mr: 2 }} />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {formatNumber(analytics.totalImpressions || 0)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Views
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: "100%", borderRadius: 2, boxShadow: 2 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <ClicksIcon
                    sx={{ color: "success.main", fontSize: 40, mr: 2 }}
                  />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {formatNumber(analytics.totalClicks || 0)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Clicks
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: "100%", borderRadius: 2, boxShadow: 2 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <TrendingUpIcon
                    sx={{ color: "warning.main", fontSize: 40, mr: 2 }}
                  />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {analytics.averageCTR?.toFixed(2) || "0.00"}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Avg CTR
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters Section */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 1 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search ads by title or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {["all", "active", "paused", "completed"].map((status) => (
                <Chip
                  key={status}
                  label={status.charAt(0).toUpperCase() + status.slice(1)}
                  onClick={() => setStatusFilter(status)}
                  color={statusFilter === status ? "primary" : "default"}
                  sx={{ textTransform: "capitalize", fontWeight: 600 }}
                />
              ))}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Ads List */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : filteredAds.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: "center", borderRadius: 2 }}>
          <Typography variant="h6" color="text.secondary">
            No ads found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {searchQuery || statusFilter !== "all"
              ? "Try adjusting your filters"
              : "Create your first ad to get started"}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredAds.map((ad) => (
            <Grid item xs={12} md={6} lg={4} key={ad.id}>
              <Card
                sx={{
                  height: "100%",
                  borderRadius: 2,
                  boxShadow: 2,
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 4,
                  },
                }}
              >
                <CardContent>
                  {/* Ad Header */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                      {ad.title}
                    </Typography>
                    <Chip
                      label={ad.status}
                      size="small"
                      color={getStatusColor(ad.status)}
                      sx={{ textTransform: "capitalize" }}
                    />
                  </Box>

                  {/* Ad Description */}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {ad.description}
                  </Typography>

                  {/* Ad Stats */}
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Box
                        sx={{
                          textAlign: "center",
                          p: 1,
                          bgcolor: "grey.50",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {formatNumber(ad.impressions || 0)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Views
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box
                        sx={{
                          textAlign: "center",
                          p: 1,
                          bgcolor: "grey.50",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {formatNumber(ad.clicks || 0)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Clicks
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Progress Bar */}
                  {ad.impressionGoal && (
                    <Box sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Progress
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {ad.impressions || 0} / {ad.impressionGoal}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          height: 8,
                          bgcolor: "grey.200",
                          borderRadius: 1,
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            height: "100%",
                            width: `${calculateProgress(
                              ad.impressions,
                              ad.impressionGoal
                            )}%`,
                            bgcolor: "primary.main",
                            transition: "width 0.3s",
                          }}
                        />
                      </Box>
                    </Box>
                  )}

                  {/* Action Buttons */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(ad)}
                          sx={{ bgcolor: "grey.100" }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {isSuperAdmin() && (
                        <>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEditAd(ad)}
                              sx={{ bgcolor: "grey.100" }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(ad)}
                              sx={{ bgcolor: "grey.100", color: "error.main" }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                    {isSuperAdmin() && (
                      <Box>
                        {ad.status?.toLowerCase() === "active" ? (
                          <Tooltip title="Pause Ad">
                            <IconButton
                              size="small"
                              onClick={() => handlePauseAd(ad.id)}
                              sx={{
                                bgcolor: "warning.light",
                                color: "warning.dark",
                              }}
                            >
                              <PauseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Activate Ad">
                            <IconButton
                              size="small"
                              onClick={() => handleActivateAd(ad.id)}
                              sx={{
                                bgcolor: "success.light",
                                color: "success.dark",
                              }}
                            >
                              <ActivateIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Modals */}
      {isSuperAdmin() && (
        <>
          <CreateAdModal
            open={openCreateModal}
            onClose={() => setOpenCreateModal(false)}
            onSuccess={handleCreateSuccess}
          />

          <EditAdModal
            open={openEditModal}
            onClose={() => {
              setOpenEditModal(false);
              setSelectedAd(null);
            }}
            ad={selectedAd}
            onSuccess={handleEditSuccess}
          />

          <DeleteConfirmDialog
            open={openDeleteDialog}
            onClose={() => {
              setOpenDeleteDialog(false);
              setSelectedAd(null);
            }}
            onConfirm={handleDeleteAd}
            adTitle={selectedAd?.title}
          />
        </>
      )}

      <AdDetailsModal
        open={openDetailsModal}
        onClose={() => {
          setOpenDetailsModal(false);
          setSelectedAd(null);
        }}
        ad={selectedAd}
      />
    </Box>
  );
};

export default AdManagement;
